import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { K } from '../kabinet/atoms';
import { D } from '../kabinet/dobavki';
import { Выпадение } from '../ui/dropdown';
import { IconChevron, IconPlus } from '../ui/icons';
import { useCabinetsOverview } from '../api/hooks';
import { cabinetName } from '../cabinet/cabinets-text';
import { короткоОКлюче } from '../cabinet/vybor-text';
import { cabinetPath } from '../routes/params';
import type { CabinetCard } from '../api/types';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ВЫБОР КАБИНЕТА В ШАПКЕ — КНОПКА С ВЫПАДЕНИЕМ, А НЕ ДВА `select`.
 *
 * До пакета 25.08 в шапке стояли два выпадающих списка подряд:
 * пространство и кабинет. Плюс имя кабинета отдельной строкой рядом.
 * Плюс ВТОРОЕ имя того же кабинета — для тех, у кого он один. Итого
 * одно и то же название печаталось дважды, а рядом с ним стоял
 * системный `select`, который в тёмной теме рисуется браузером
 * по-своему и ни одной переменной кабинета не слушает.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ГЛАВНОЕ, ЧТО ДОБАВИЛОСЬ, — СОСТОЯНИЕ КЛЮЧА У КАЖДОЙ СТРОКИ
 *
 * `select` умеет показать только название. А человеку, у которого три
 * кабинета, важно не «какой из них выбран», а «какой из них СЛОМАН»:
 * ключ просрочен — данные по этому кабинету стоят, и стоят молча.
 *
 * Поэтому в выпадении под каждым именем идёт точка и строка словами:
 * «ключ работает · до 12.02.2027», «кончится через 9 дней»,
 * «не отвечает — обновите». Текст и цвет считает сервер (`token.text`,
 * `token.health`) — он же считает их на экране «Кабинеты», и два
 * счёта разошлись бы.
 *
 * ЧИСЛО «3 из 5» СТОИТ НА САМОЙ КНОПКЕ. Предел тарифа человек узнаёт
 * не тогда, когда открывает список, а тогда, когда упирается в отказ
 * подключить четвёртый. Число рядом с названием отвечает заранее.
 * ═══════════════════════════════════════════════════════════════════════
 */
export function HeadCabinets({ wsId, cabinetId, afterTable = false }: { wsId: string; cabinetId: string | undefined; afterTable?: boolean }) {
  const [открыт, setОткрыт] = useState(false);
  const navigate = useNavigate();
  const q = useCabinetsOverview(wsId, { afterTable: afterTable ? cabinetId : undefined, immediate: открыт });

  const кабинеты = q.data?.cabinets ?? [];
  const текущий = кабинеты.find((c) => c.id === cabinetId) ?? null;

  /**
   * ПОКА КАБИНЕТОВ НЕТ — КНОПКИ НЕТ. Не «Выберите кабинет» и не пустой
   * список: и то и другое предлагает нажать туда, где ничего не будет.
   * Человека без кабинета ведёт на подключение пустой биддер, и это
   * единственное место, где такое предложение к месту.
   *
   * УЖЕ 1120 — ТОЖЕ НЕТ. Эталон убирает кнопку из шапки ниже этой
   * ширины (`roomy`), и убирает раньше, чем ломается вся раскладка:
   * в шапке к этому моменту стоят пять значков справа, остаток
   * тарифа и название раздела. Правило — в `dobavki.css`.
   */
  if (текущий === null) return null;

  const место = q.data?.room ?? null;
  const счёт = место === null || место.limit === null ? null : `${место.used} из ${место.limit}`;
  const закрыть = () => setОткрыт(false);

  return (
    <span className={`k-у-выпадения ${D.толькоПросторно}`}>
      <button
        type="button"
        data-d="выбор-кабинета"
        aria-haspopup="menu"
        aria-expanded={открыт}
        className={K.выборКабинета}
        onClick={() => setОткрыт((v) => !v)}
      >
        <span className={K.имяВыбораКабинета}>{имя(текущий)}</span>
        {счёт !== null && <span className={K.счётВыбораКабинета}>{счёт}</span>}
        <IconChevron size={12} dir={открыт ? 'up' : 'down'} />
      </button>

      <Выпадение открыто={открыт} закрыть={закрыть}>
        <span role="menu" data-d="меню-кабинетов" className={K.менюКабинетов}>
          {кабинеты.map((c) => (
            <button
              key={c.id}
              type="button"
              role="menuitem"
              className={K.строкаКабинетаВМеню}
              style={c.id === cabinetId ? { background: 'var(--brand-wash)' } : undefined}
              onClick={() => {
                закрыть();
                navigate(cabinetPath(wsId, c.id));
              }}
            >
              <span
                className={K.имяКабинетаВМеню}
                style={
                  c.id === cabinetId
                    ? { color: 'var(--brand)', fontWeight: 600 }
                    : undefined
                }
              >
                {имя(c)}
              </span>

              {/* СОСТОЯНИЕ КЛЮЧА ПОКАЗЫВАЕТСЯ, ТОЛЬКО КОГДА ЕСТЬ ЧТО
                  СКАЗАТЬ. `token === null` — это «мы ещё не спрашивали»,
                  а не «плохо»; серая точка без слов в списке из трёх
                  кабинетов читается как «у этого что-то не так».

                  А ВОТ ИСПРАВНЫЙ КЛЮЧ СКАЗАТЬ ЕСТЬ ЧТО — и до 08.09.2026
                  он молчал: условие стояло на `token.text`, который
                  служба у здорового ключа не заполняет вовсе. В списке
                  из трёх кабинетов строка была у сломанных и не было
                  у рабочих — то есть ровно там, где сравнивать. Тем же
                  счётом, что и карточка: `короткоОКлюче`. */}
              {c.token !== null && строкаКлюча(c) !== null && (
                <span className={K.состояниеКлюча} title={c.token.text ?? undefined}>
                  <span aria-hidden="true" className={K.точкаКлюча} style={{ background: цветКлюча(c) }} />
                  {строкаКлюча(c)}
                </span>
              )}
            </button>
          ))}

          <span aria-hidden="true" className={K.чертаПрофиля} />

          {счёт !== null && <span className={K.подписьМенюКабинетов}>{счёт} по тарифу</span>}

          <Link
            to={`/rk/${wsId}/connect`}
            data-d="кнопка-добавить-кабинет"
            role="menuitem"
            className={`${K.кнопкаДобавитьКабинет} no-underline`}
            onClick={закрыть}
          >
            <IconPlus size={14} />
            Добавить кабинет
          </Link>

          {/* ═══════════════════════════════════════════════════════
              АДРЕС — ВНУТРИ КАБИНЕТА, А НЕ ПРЯМО ПОД ПРОСТРАНСТВОМ.

              08.09.2026 владелец: «тут кнопка все кабинеты не работает».
              И не работала: `/rk/:wsId/cabinets` такого маршрута нет
              вовсе. Разбор адресов принимал `cabinets` за ОПОЗНАВАТЕЛЬ
              КАБИНЕТА в `/rk/:wsId/:cabinetId` — и открывал биддер
              несуществующего кабинета. Не отказ, не пустая страница,
              а тихо не то место: худший из трёх исходов.

              Раздел живёт по двум адресам сразу (довод — в `app.tsx`):
              внутри кабинета и в настройках пространства. Из шапки
              ведём ВНУТРЬ: человек не выходил из кабинета и не должен
              оказаться в рамке без его меню.
              ═══════════════════════════════════════════════════════ */}
          <Link
            to={`${cabinetPath(wsId, текущий.id)}/cabinets`}
            data-d="пункт-все-кабинеты"
            role="menuitem"
            className={`${K.пунктВыйти} no-underline`}
            onClick={закрыть}
          >
            Все кабинеты и ключи
          </Link>
        </span>
      </Выпадение>
    </span>
  );
}

/** Короткая строка о ключе — тем же счётом, что на карточке кабинета. */
function строкаКлюча(c: CabinetCard): string | null {
  if (c.token === null) return null;
  return короткоОКлюче(c.token.health, c.token.expiresAt);
}

function имя(c: CabinetCard): string {
  return cabinetName(c.displayName, c.tradeMark, c.wbOwnerId);
}

/**
 * Цвет точки берётся из ЗДОРОВЬЯ ключа, а не из текста рядом.
 *
 * Текст сочиняет сервер и меняет по поводу; цвет — это ступень,
 * и ступеней ровно три. Считать цвет по словам («если в строке есть
 * „не отвечает“ — красный») значит завести разбор чужой строки там,
 * где рядом лежит готовый признак.
 */
function цветКлюча(c: CabinetCard): string {
  switch (c.token?.health) {
    case 'ok':
      return 'var(--good)';
    case 'soon':
      return 'var(--warn)';
    default:
      return 'var(--bad)';
  }
}
